import { useEffect } from "react";
import { PAGE_SEARCH_ATTR } from "@/components/SearchInput/pageSearch";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { shouldHandleAppChord } from "@/lib/keyboardShortcuts";

export { PAGE_SEARCH_ATTR };

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "url", "tel", "textarea"]);
const MATERIAL_TEXT_FIELD_SELECTOR = "md-outlined-text-field, md-filled-text-field";

function isVisible(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    if (current.hidden) return false;
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    current = current.parentElement;
  }
  return true;
}

function isUsableTextControl(element: HTMLElement): boolean {
  if (!isVisible(element)) return false;
  if (element.closest("[inert]")) return false;
  return true;
}

function isAllowedTextInputType(type: string): boolean {
  return TEXT_INPUT_TYPES.has(type.toLowerCase());
}

function materialHostInputType(host: HTMLElement): string {
  const attr = host.getAttribute("type");
  if (attr) return attr;
  const prop = Reflect.get(host, "type");
  return typeof prop === "string" && prop.length > 0 ? prop : "text";
}

export function findPageSearchInput(root: ParentNode = document): HTMLInputElement | null {
  const candidates = root.querySelectorAll<HTMLInputElement>(`input[${PAGE_SEARCH_ATTR}]`);
  for (const input of candidates) {
    if (input.disabled || input.readOnly) continue;
    if (!isVisible(input)) continue;
    return input;
  }
  return null;
}

function collectVisibleTextFields(root: ParentNode): HTMLElement[] {
  const fields: HTMLElement[] = [];

  for (const input of root.querySelectorAll<HTMLInputElement>("input")) {
    if (input.disabled || input.readOnly) continue;
    if (!isAllowedTextInputType(input.type)) continue;
    if (!isUsableTextControl(input)) continue;
    fields.push(input);
  }

  for (const textarea of root.querySelectorAll<HTMLTextAreaElement>("textarea")) {
    if (textarea.disabled || textarea.readOnly) continue;
    if (!isUsableTextControl(textarea)) continue;
    fields.push(textarea);
  }

  for (const host of root.querySelectorAll<HTMLElement>(MATERIAL_TEXT_FIELD_SELECTOR)) {
    if (host.hasAttribute("disabled") || host.hasAttribute("readonly")) continue;
    if (!isAllowedTextInputType(materialHostInputType(host))) continue;
    if (!isUsableTextControl(host)) continue;
    fields.push(host);
  }

  for (const editable of root.querySelectorAll<HTMLElement>("[contenteditable]")) {
    if (editable.getAttribute("contenteditable") === "false") continue;
    if (editable.parentElement?.closest("[contenteditable]")) continue;
    if (!isUsableTextControl(editable)) continue;
    fields.push(editable);
  }

  return fields;
}

/** The only visible, enabled text field on the page, or null when there is not exactly one. */
export function findSoleVisibleTextField(root: ParentNode = document): HTMLElement | null {
  const fields = collectVisibleTextFields(root);
  return fields.length === 1 ? fields[0] : null;
}

function selectIfSingleLine(element: HTMLInputElement | HTMLTextAreaElement): void {
  if (element instanceof HTMLInputElement) {
    element.select();
  }
}

function focusTextField(element: HTMLElement): void {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    element.focus();
    selectIfSingleLine(element);
    return;
  }

  const shadowInput = element.shadowRoot?.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
  if (shadowInput) {
    shadowInput.focus();
    selectIfSingleLine(shadowInput);
    return;
  }

  element.focus();
}

/** Focus and select the first visible page search input. Returns false when none is ready. */
export function focusPageSearchInput(): boolean {
  const input = findPageSearchInput();
  if (!input) return false;
  focusTextField(input);
  return true;
}

function focusSoleVisibleTextField(): boolean {
  const field = findSoleVisibleTextField();
  if (!field) return false;
  focusTextField(field);
  return true;
}

function shouldIgnoreAppShortcut(event: KeyboardEvent): boolean {
  if (!shouldHandleAppChord(event)) return true;
  if (hasOpenOverlay() || hasTransientPopupOpen()) return true;
  if (document.body.dataset.goNavMode === "true") return true;
  return false;
}

function isQuestionMarkKey(event: KeyboardEvent): boolean {
  return event.key === "?" || (event.key === "/" && event.shiftKey);
}

/**
 * `/` focuses the first page SearchInput (`data-page-search`). When none is present,
 * it focuses the page's sole visible text field (native input, Material text field
 * including textarea, or contenteditable). Suppressed while overlays, menus, or go-nav
 * mode are active. Shift+/ is handled as chat (`?`), not search.
 */
export function useSlashShortcut() {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreAppShortcut(event)) return;
      if (event.key !== "/" || event.shiftKey) return;

      if (focusPageSearchInput() || focusSoleVisibleTextField()) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);
}

/**
 * `?` opens Chat with Member 360 when `onOpenMcpPlayground` is provided. Suppressed while overlays,
 * menus, or go-nav mode are active. Keyboard-shortcuts help remains ⌘/Ctrl+Shift+?.
 */
export function useQuestionMarkChatShortcut(onOpenMcpPlayground?: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!onOpenMcpPlayground) return;
      if (shouldIgnoreAppShortcut(event)) return;
      if (!isQuestionMarkKey(event)) return;

      event.preventDefault();
      onOpenMcpPlayground();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onOpenMcpPlayground]);
}
